// KPIs › Issues at `/c/{slug}/kpis/issues` (MEETINGS): the issues board. The list (`?team=`
// a team id or `company`; absent = all the caller sees) and the view (`?view=archived`) are in
// the URL beside the focus. Open to whoever may open the scorecard or the chart (`CadenceGate`); the
// server cuts the board to what the caller may see. FOUNDER-OS phase 1: in Founder OS the page is
// the escalated board and the portfolio's own issues (was Portfolio › Issues); `?board=own`
// opens this board for the portfolio company's own issues.
import { createRoute, useNavigate } from "@tanstack/react-router";
import { CadenceGate } from "../features/cadence/cadence-parts";
import { IssuesScreen } from "../features/cadence/issues-screen";
import { PortfolioIssuesScreen } from "../features/portfolio/portfolio-issues";
import { useCompany } from "../features/use-feature";
import { validateFocusSearch } from "../focus/focus";
import { kpisRoute } from "./c.$slug.kpis";

const ID = /^[0-9a-f-]{36}$/i;

export const issuesRoute = createRoute({
  getParentRoute: () => kpisRoute,
  path: "issues",
  validateSearch: (search: Record<string, unknown>) => ({
    ...validateFocusSearch(search),
    ...(typeof search.team === "string" && (search.team === "company" || ID.test(search.team))
      ? { team: search.team }
      : {}),
    ...(search.view === "archived" ? { view: "archived" as const } : {}),
    ...(search.board === "own" ? { board: "own" as const } : {}),
  }),
  component: function Issues() {
    const { api } = issuesRoute.useRouteContext();
    const { team, view, board } = issuesRoute.useSearch();
    const navigate = useNavigate();
    const company = useCompany();
    // FOUNDER-OS phase 1: in Founder OS, the issues escalated to the founder and the portfolio's
    // own; the ordinary board for its own issues is `?board=own`.
    if (company.kind === "portfolio" && board !== "own")
      return (
        <CadenceGate what="Issues">
          <PortfolioIssuesScreen api={api} company={company} />
        </CadenceGate>
      );
    return (
      <CadenceGate what="Issues">
        <IssuesScreen
          api={api}
          company={company}
          team={team ?? "all"}
          view={view ?? "board"}
          onSearch={(next) =>
            void navigate({
              to: ".",
              search: (prev: Record<string, unknown>) => {
                const out: Record<string, unknown> = { ...prev };
                for (const k of ["team", "view"] as const)
                  if (k in next) {
                    if (next[k] === undefined) delete out[k];
                    else out[k] = next[k];
                  }
                return out;
              },
              replace: true,
            } as never)
          }
        />
      </CadenceGate>
    );
  },
});
